import { ConnectionQualityEnum, NetworkStats, ParsedWebRTCStats, TrackStats } from '@matap/shared';

export class WebrtcStatsExtractor {
  private previousStats = new Map<string, any>();

  // State Caches moved here from P2PPeer
  public lastKnownStats?: ParsedWebRTCStats;
  private lastEmittedQuality?: ConnectionQualityEnum;

  constructor(private pc: RTCPeerConnection) {}

  public async extract(isAudioOnly: boolean = false): Promise<ParsedWebRTCStats | undefined> {
    const statsResult = await this.pc.getStats().catch(() => null);
    if (!statsResult) return undefined;

    const snapshot: ParsedWebRTCStats = {
      video: { send: this.getEmptyTrackStats(), recv: this.getEmptyTrackStats() },
      audio: { send: this.getEmptyTrackStats(), recv: this.getEmptyTrackStats() },
      network: { rtt: 0 },
    };

    const reports: any[] = [];
    const candidatePairs: Record<string, any> = {};
    const localCandidates: Record<string, any> = {};
    const remoteCandidates: Record<string, any> = {};
    const codecs: Record<string, string> = {};

    statsResult.forEach((report: any) => {
      if (Array.isArray(report.values)) {
        const flatReport = { ...report };
        report.values.forEach((v: any) => Object.assign(flatReport, v));
        reports.push(flatReport);
      } else {
        reports.push(report);
      }
    });

    reports.forEach((report) => {
      if (report.type === 'local-candidate') localCandidates[report.id] = report;
      if (report.type === 'remote-candidate') remoteCandidates[report.id] = report;
      if (report.type === 'candidate-pair') candidatePairs[report.id] = report;
      if (report.type === 'codec') {
        codecs[report.id] = report.mimeType ? report.mimeType.split('/')[1].toLowerCase() : 'unknown';
      }
    });

    reports.forEach((report) => {
      const kind = report.kind || report.mediaType;
      const isVideo = kind === 'video';
      const isAudio = kind === 'audio';

      if (report.type === 'inbound-rtp' && (isVideo || isAudio)) {
        this.parseRTP(report, isVideo ? snapshot.video.recv : snapshot.audio.recv, true, codecs, snapshot.network);
      } else if (report.type === 'outbound-rtp' && (isVideo || isAudio)) {
        this.parseRTP(report, isVideo ? snapshot.video.send : snapshot.audio.send, false, codecs, snapshot.network);
      } else if (report.type === 'track' && isAudio && report.remoteSource === true) {
        if (report.audioLevel !== undefined) snapshot.audio.recv.audioLevel = report.audioLevel;
      } else if (report.type === 'transport' && report.selectedCandidatePairId) {
        this.parseNetwork(report.selectedCandidatePairId, candidatePairs, localCandidates, remoteCandidates, snapshot.network);
      } else if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
        this.parseNetwork(report.id, candidatePairs, localCandidates, remoteCandidates, snapshot.network);
      }
    });

    snapshot.score = this.calculateOverallScore(snapshot);

    if (snapshot.score >= 90) {
      snapshot.quality = ConnectionQualityEnum.excellent;
    } else if (snapshot.score >= 70) {
      snapshot.quality = ConnectionQualityEnum.fair;
    } else if (snapshot.score >= 40) {
      snapshot.quality = ConnectionQualityEnum.poor;
    } else {
      snapshot.quality = ConnectionQualityEnum.bad;
    }

    if (isAudioOnly) {
      delete (snapshot as any).video;
    }

    // Cache to local memory
    this.lastKnownStats = snapshot;

    return snapshot;
  }

  /**
   * Acts as an anomaly gate. Returns a minimal diagnostic payload ONLY if the quality bucket
   * has changed since the last emission. Includes bandwidth data for debugging throttles.
   */
  public buildAnomalyPayload(isAudioOnly: boolean): any | null {
    const stats = this.lastKnownStats;
    if (!stats) return null;

    if (stats.quality !== this.lastEmittedQuality) {
      this.lastEmittedQuality = stats.quality;

      const minimalDiagnosticPayload: any = {
        score: stats.score,
        quality: stats.quality,
        network: {
          rtt: stats.network.rtt,
          availableOutgoingBitrate: stats.network.availableOutgoingBitrate, // Bandwidth diagnostic!
        },
      };

      if (isAudioOnly) {
        minimalDiagnosticPayload.audio = {
          send: { bitrate: stats.audio.send.bitrate }, // Outbound bandwidth
          recv: {
            bitrate: stats.audio.recv.bitrate, // Inbound bandwidth
            packetLossRatio: stats.audio.recv.packetLossRatio,
            jitter: stats.audio.recv.jitter,
          },
        };
      } else {
        minimalDiagnosticPayload.video = {
          send: { bitrate: stats.video?.send.bitrate },
          recv: {
            bitrate: stats.video?.recv.bitrate,
            packetLossRatio: stats.video?.recv.packetLossRatio,
            jitter: stats.video?.recv.jitter,
          },
        };
      }

      return minimalDiagnosticPayload;
    }

    return null;
  }

  private calculateOverallScore(snapshot: ParsedWebRTCStats): number {
    let score = 100;

    const worstLossRatio = Math.max(snapshot.audio.recv.packetLossRatio || 0, snapshot.video.recv.packetLossRatio || 0);
    const worstJitter = Math.max(snapshot.audio.recv.jitter || 0, snapshot.video.recv.jitter || 0);
    const rtt = snapshot.network.rtt || 0;

    score -= worstLossRatio * 1000;

    if (rtt > 100) {
      score -= (rtt - 100) / 10;
    }

    if (worstJitter > 20) {
      score -= (worstJitter - 20) / 2;
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  private parseRTP(report: any, stats: TrackStats, isInbound: boolean, codecs: Record<string, string>, network: NetworkStats) {
    const id = report.id;
    const now = report.timestamp;
    const currentBytes = isInbound ? report.bytesReceived : report.bytesSent;
    const currentPackets = isInbound ? report.packetsReceived : report.packetsSent;
    const currentPacketsLost = report.packetsLost || 0;
    const previous = this.previousStats.get(id);

    if (previous && previous.bytes && currentBytes) {
      const ms = now - previous.timestamp;
      if (ms > 0 && currentBytes >= previous.bytes) {
        stats.bitrate = Math.round(((currentBytes - previous.bytes) * 8) / ms);
      }
    }
    if (previous && currentPackets) {
      const pDiff = currentPackets - (previous.packets || 0);
      const lDiff = currentPacketsLost - (previous.packetsLost || 0);
      if (pDiff > 0) stats.packetLossRatio = parseFloat((lDiff / (pDiff + lDiff)).toFixed(4));
    }

    stats.bytes = currentBytes || 0;
    if (report.codecId && codecs[report.codecId]) stats.codec = codecs[report.codecId];

    stats.jitter = report.jitter ? Math.round(report.jitter * 1000) : 0;

    let currentBufferDelay = 0;
    if (report.jitterBufferDelay && report.jitterBufferEmittedCount) {
      currentBufferDelay = Math.round((report.jitterBufferDelay / report.jitterBufferEmittedCount) * 1000);
    }

    stats.bufferLatency = currentBufferDelay;
    stats.networkLatency = Math.round((network.rtt || 0) / 2);
    stats.totalLatency = stats.networkLatency + stats.bufferLatency;

    if (report.kind === 'video' && stats.framerate) {
      stats.framesBehind = parseFloat(((stats.bufferLatency / 1000) * stats.framerate).toFixed(1));
    }

    stats.packetsLost = currentPacketsLost;
    if (report.framesPerSecond !== undefined) stats.framerate = Math.round(report.framesPerSecond);

    if (report.kind === 'video') {
      stats.framesDropped = report.framesDropped || 0;
      stats.framesDecoded = report.framesDecoded || 0;
      const w = report.frameWidth || report.frameWidthReceived;
      const h = report.frameHeight || report.frameHeightReceived;
      if (w && h) stats.resolution = `${w}x${h}`;
    }

    if (report.kind === 'audio') {
      stats.concealedSamples = report.concealedSamples || 0;
      if (report.audioLevel !== undefined) stats.audioLevel = report.audioLevel;
    }

    this.previousStats.set(id, { timestamp: now, bytes: currentBytes, packets: currentPackets, packetsLost: currentPacketsLost });
  }

  private parseNetwork(pairId: string, candidatePairs: Record<string, any>, locals: Record<string, any>, remotes: Record<string, any>, networkOut: NetworkStats) {
    const pair = candidatePairs[pairId];
    if (!pair) return;

    networkOut.rtt = pair.currentRoundTripTime ? Math.round(pair.currentRoundTripTime * 1000) : 0;

    if (pair.availableOutgoingBitrate) {
      networkOut.availableOutgoingBitrate = Math.round(pair.availableOutgoingBitrate / 1000);
    }

    const local = locals[pair.localCandidateId];
    const remote = remotes[pair.remoteCandidateId];

    if (local) {
      networkOut.localIp = local.ip || local.address || local.ipAddress;
      networkOut.protocol = local.protocol;
      networkOut.candidateType = local.candidateType;
    }
    if (remote) {
      networkOut.remoteIp = remote.ip || remote.address || remote.ipAddress;
    }
  }

  private getEmptyTrackStats(): TrackStats {
    return {
      bitrate: 0,
      bytes: 0,
      packetLossRatio: 0,
      packetsLost: 0,
      jitter: 0,
      delay: 0,
      framesDropped: 0,
      framesDecoded: 0,
      networkLatency: 0,
      bufferLatency: 0,
      totalLatency: 0,
      framesBehind: 0,
    };
  }

  public clear() {
    this.previousStats.clear();
  }
}
